import { useInfiniteQuery } from '@tanstack/react-query';
import { getBooksByTag } from '../api/tags';
import type { TagBookItem, TagBooksResponse } from '../api/tags';
import { useMemo } from 'react';

interface UseBooksByTagOptions {
  enabled?: boolean;
  limit?: number;
}

export const useBooksByTag = (
  tagId: number | null,
  options: UseBooksByTagOptions = {}
) => {
  const { enabled = true, limit = 20 } = options;

  const query = useInfiniteQuery({
    queryKey: ['books', 'tag', tagId, limit],
    queryFn: async ({ pageParam = 1 }) => {
      return getBooksByTag(tagId!, { page: pageParam, limit });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination?.has_next) {
        return (lastPage.pagination.page || 1) + 1;
      }
      return undefined;
    },
    enabled: !!tagId && enabled,
    staleTime: 30_000,
  });

  const books = useMemo(() => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page.books || []);
  }, [query.data?.pages]);

  const totalCount = query.data?.pages[0]?.pagination?.total_count ?? 0;
  const tagName = query.data?.pages[0]?.books?.[0]?.tag_name;

  return {
    books,
    totalCount,
    tagName,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isError: query.isError,
    error: query.error,
  };
};
